import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../guias/guiasUtils';
import { STATUS_AUD, brl } from './AuditoriaPaciente';

const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const CATS = [['medicamento', 'Medicamentos'], ['material', 'Materiais'], ['dieta', 'Dietas'], ['equipamento', 'Equipamentos'], ['sem_categoria', 'Sem categoria']];

export const CompetenciaTab = ({ api, onOpen }) => {
  const [comp, setComp] = useState(mesAtual());
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!comp) return;
    setBusy(true);
    try { setD((await api.get(`/auditoria-financeira/competencia/${comp}`)).data); } catch (e) { toast.error(errMsg(e, 'Erro ao carregar competência')); } finally { setBusy(false); }
  }, [api, comp]);
  useEffect(() => { load(); }, [load]);

  const pdf = async () => {
    try {
      const r = await api.get(`/auditoria-financeira/competencia/${comp}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `consolidado_financeiro_${comp}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, 'Erro ao gerar PDF')); }
  };

  const t = d?.totais;
  return (
    <div className="space-y-3" data-testid="af-competencia">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" className="w-44" value={comp} onChange={(e) => setComp(e.target.value)} data-testid="af-comp-input" />
        <Button variant="outline" onClick={pdf} disabled={!d} data-testid="af-comp-pdf"><FileText className="h-4 w-4 mr-2" />PDF consolidado</Button>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {d?.aviso && <Badge variant="outline" className="bg-amber-100 text-amber-900 border-amber-300" data-testid="af-comp-aviso">{d.aviso} ({t.nao_conciliados})</Badge>}
      </div>
      {t && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-sm">
            {[['atendidos', 'Pacientes atendidos', t.pacientes_atendidos], ['ativos', 'Ativos no fim do mês', t.pacientes_ativos], ['alta', 'Com alta no mês', t.pacientes_com_alta], ['dias', 'Dias de atendimento', t.dias],
              ['medicamento', 'Medicamentos', brl(t.medicamento)], ['material', 'Materiais', brl(t.material)], ['dieta', 'Dietas', brl(t.dieta)], ['equipamento', 'Equipamentos', brl(t.equipamento)], ['sem-categoria', 'Sem categoria', brl(t.sem_categoria)],
              ['devolucoes', 'Devoluções (abatidas)', brl(t.devolucoes)], ['assistencial', 'Custo assistencial realizado', t.custo_assistencial == null ? 'sem base de custo' : brl(t.custo_assistencial)], ['previsto', 'Previsto (serviços)', brl(t.previsto_servicos)],
              ['total', 'CUSTO TOTAL REALIZADO', brl(t.custo_total), 'font-semibold text-emerald-800'], ['medio-pac', 'Custo médio por paciente', brl(t.custo_medio_paciente)], ['medio-dia', 'Custo médio diário', brl(t.custo_medio_diario)], ['confere', 'Soma individual confere', t.soma_individual_confere ? 'sim' : 'NÃO', t.soma_individual_confere ? '' : 'text-rose-700']].map(([k, l, v, c]) => (
                <div key={k} className="rounded border p-2" data-testid={`af-comp-${k}`}><p className="text-[11px] text-muted-foreground">{l}</p><p className={c || ''}>{v}</p></div>))}
          </div>
          <Card><CardContent className="p-0 overflow-x-auto"><table className="w-full text-sm" data-testid="af-comp-tabela">
            <thead className="bg-muted/50"><tr><th className="text-left p-2">Paciente</th><th className="p-2">Prontuário</th><th className="p-2">Dias</th>{CATS.map(([k, l]) => <th key={k} className="p-2">{l}</th>)}<th className="p-2">Realizado</th><th className="p-2">Previsto</th><th className="text-left p-2">Status</th></tr></thead>
            <tbody>{d.pacientes.map((p) => (
              <tr key={p.internacao_id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => onOpen(p.internacao_id)} data-testid={`af-comp-linha-${p.internacao_id}`}>
                <td className="p-2 font-medium">{p.paciente_nome}</td><td className="p-2 text-center font-mono text-xs">{p.numero_prontuario || '—'}</td><td className="p-2 text-center">{p.dias}</td>
                {CATS.map(([k]) => <td key={k} className="p-2 text-right">{brl(p.por_categoria[k])}</td>)}
                <td className="p-2 text-right font-semibold">{brl(p.realizado_liq)}</td><td className="p-2 text-right text-muted-foreground">{brl(p.previsto_servicos)}</td>
                <td className="p-2"><Badge variant="outline" className={STATUS_AUD[p.status]?.[1]}>{STATUS_AUD[p.status]?.[0]}</Badge></td>
              </tr>))}
              {!d.pacientes.length && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground" data-testid="af-comp-vazio">Nenhum paciente elegível nesta competência.</td></tr>}</tbody>
          </table></CardContent></Card>
        </>)}
    </div>
  );
};
